package dev.danko.roblox_craft_ai.pages

import dev.danko.roblox_craft_ai.components.SiteSection
import dev.danko.roblox_craft_ai.components.SiteHeader
import dev.danko.roblox_craft_ai.components.SiteFooter
import dev.danko.roblox_craft_ai.components.LegalDoc
import dev.danko.roblox_craft_ai.components.LegalLinks
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.client.PaddleCheckout
import dev.danko.roblox_craft_ai.client.RobloxCraftApiClient
import dev.danko.roblox_craft_ai.components.layouts.PageLayoutData
import dev.danko.roblox_craft_ai.shared.model.BillingCatalogDto
import dev.danko.roblox_craft_ai.shared.model.BillingStatusDto
import dev.danko.roblox_craft_ai.state.AuthState
import dev.danko.roblox_craft_ai.state.Txt
import dev.danko.roblox_craft_ai.uikit.components.AppButton
import dev.danko.roblox_craft_ai.uikit.components.AppButtonSize
import dev.danko.roblox_craft_ai.uikit.components.AppButtonVariant
import dev.danko.roblox_craft_ai.uikit.components.AppCard
import dev.danko.roblox_craft_ai.uikit.components.LanguageSwitcher
import dev.danko.roblox_craft_ai.uikit.components.ToastManager
import dev.danko.roblox_craft_ai.uikit.i18n.LocaleController
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

@InitRoute
@Suppress("unused")
fun initPricingPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(Txt.billing.pageTitle))
}

private fun formatPrice(cents: Int, currency: String, locale: String): String {
    val options: dynamic = js("({})")
    options.style = "currency"
    options.currency = currency
    return (cents / 100.0).asDynamic().toLocaleString(locale, options) as String
}

private fun formatDate(ms: Long, locale: String): String = kotlin.js.Date(ms.toDouble()).toLocaleDateString(locale)

@Composable
@Page("/pricing")
fun PricingPage() {
    val s = strings().billing
    val palette = LocalAppTheme.current.colors
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val locale = LocaleController.current.code
    var catalog by remember { mutableStateOf<BillingCatalogDto?>(null) }
    var status by remember { mutableStateOf<BillingStatusDto?>(null) }
    val user = AuthState.user

    suspend fun reload() {
        catalog = RobloxCraftApiClient.getBillingCatalog()
        status = if (AuthState.user != null) RobloxCraftApiClient.getBillingStatus() else null
        AuthState.refreshUsage()
    }

    LaunchedEffect(user?.id) { reload() }

    fun checkout(priceId: String?) {
        val config = catalog?.paddle
        val currentUser = AuthState.user
        if (currentUser == null) {
            AuthState.loginDialogOpen = true
            return
        }
        if (config == null || !config.enabled || priceId.isNullOrBlank()) {
            ToastManager.info(s.paymentSoon)
            return
        }
        scope.launch {
            val opened = PaddleCheckout.open(config, priceId, currentUser.email, currentUser.id, locale) {
                ToastManager.success(s.checkoutDone)
                // The webhook usually lands within seconds; refresh a few times to pick it up.
                scope.launch {
                    repeat(5) {
                        delay(3000)
                        reload()
                    }
                }
            }
            if (!opened) ToastManager.error(s.checkoutFailed)
        }
    }

    SiteHeader(SiteSection.PRICING)
    Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.TopCenter) {
        Column(Modifier.fillMaxWidth().maxWidth(72.cssRem).padding(1.cssRem).gap(1.5.cssRem)) {


            Column(Modifier.gap(0.5.cssRem)) {
                SpanText(s.title, Modifier.fontSize(1.8.cssRem).fontWeight(FontWeight.Bold).color(palette.textPrimary))
                SpanText(s.subtitle, Modifier.fontSize(0.95.cssRem).color(palette.textSecondary))
                val usage = AuthState.usage
                val limit = usage?.creditsLimit
                if (usage != null && limit != null) {
                    SpanText(
                        "⚡ " + s.balance((limit - usage.creditsUsed).coerceAtLeast(0).toString(), usage.purchasedCredits),
                        Modifier.fontSize(0.9.cssRem).color(palette.textPrimary)
                    )
                }
            }

            val currentStatus = status
            if (currentStatus != null && (currentStatus.cancelUrl != null || currentStatus.updatePaymentUrl != null)) {
                AppCard(Modifier.fillMaxWidth()) {
                    Column(Modifier.gap(0.6.cssRem)) {
                        SpanText(s.manageTitle, Modifier.fontWeight(FontWeight.SemiBold).color(palette.textPrimary))
                        currentStatus.currentPeriodEndMs?.let { SpanText(s.renewsOn(formatDate(it, locale)), Modifier.color(palette.textSecondary)) }
                        if (currentStatus.subscriptionStatus == "past_due") SpanText("⚠️ ${s.pastDueHint}", Modifier.color(palette.statusFailed))
                        Row(Modifier.gap(0.5.cssRem).flexWrap(FlexWrap.Wrap)) {
                            currentStatus.updatePaymentUrl?.let { url ->
                                AppButton(text = s.updatePayment, onClick = { window.open(url, "_blank") }, variant = AppButtonVariant.OUTLINE, size = AppButtonSize.SM)
                            }
                            currentStatus.cancelUrl?.let { url ->
                                AppButton(text = s.cancelSubscription, onClick = { window.open(url, "_blank") }, variant = AppButtonVariant.OUTLINE, size = AppButtonSize.SM)
                            }
                        }
                    }
                }
            }

            val plans = catalog?.plans.orEmpty()
            Row(Modifier.fillMaxWidth().gap(1.cssRem).flexWrap(FlexWrap.Wrap), horizontalArrangement = Arrangement.Start) {
                plans.forEach { plan ->
                    val isCurrent = (status?.planId ?: "free") == plan.id && user != null
                    AppCard(
                        Modifier
                            .flexGrow(1)
                            .flexBasis(14.cssRem)
                            .minWidth(13.cssRem)
                            .border(if (isCurrent) 2.px else 0.px, LineStyle.Solid, palette.accent)
                    ) {
                        Column(Modifier.fillMaxWidth().gap(0.6.cssRem)) {
                            SpanText(plan.name, Modifier.fontSize(1.2.cssRem).fontWeight(FontWeight.Bold).color(palette.textPrimary))
                            Row(verticalAlignment = Alignment.Bottom) {
                                SpanText(
                                    if (plan.priceCents == 0) s.free else formatPrice(plan.priceCents, plan.currency, locale),
                                    Modifier.fontSize(1.6.cssRem).fontWeight(FontWeight.Bold).color(palette.textPrimary)
                                )
                                if (plan.priceCents > 0) SpanText(s.perMonth, Modifier.color(palette.textSecondary).margin(left = 0.2.cssRem))
                            }
                            SpanText(
                                plan.monthlyCredits?.let { s.creditsPerMonth(it) } ?: s.unlimitedCredits,
                                Modifier.color(palette.textSecondary)
                            )
                            when {
                                isCurrent -> SpanText("✓ ${s.currentPlan}", Modifier.color(palette.accent).fontWeight(FontWeight.SemiBold))
                                plan.priceCents > 0 -> AppButton(
                                    text = when {
                                        user == null -> s.signInToBuy
                                        status?.cancelUrl != null -> s.switchPlan
                                        else -> s.subscribe
                                    },
                                    onClick = { checkout(plan.paddlePriceId) },
                                    fullWidth = true
                                )
                            }
                        }
                    }
                }
            }

            val packs = catalog?.packs.orEmpty()
            if (packs.isNotEmpty()) {
                Column(Modifier.gap(0.5.cssRem)) {
                    SpanText(s.packsTitle, Modifier.fontSize(1.3.cssRem).fontWeight(FontWeight.Bold).color(palette.textPrimary))
                    SpanText(s.packsHint, Modifier.color(palette.textSecondary))
                }
                Row(Modifier.fillMaxWidth().gap(1.cssRem).flexWrap(FlexWrap.Wrap)) {
                    packs.forEach { pack ->
                        AppCard(Modifier.flexGrow(1).flexBasis(12.cssRem).minWidth(11.cssRem)) {
                            Column(Modifier.fillMaxWidth().gap(0.6.cssRem)) {
                                SpanText("⚡ ${s.packCredits(pack.credits)}", Modifier.fontSize(1.1.cssRem).fontWeight(FontWeight.Bold).color(palette.textPrimary))
                                SpanText(formatPrice(pack.priceCents, pack.currency, locale), Modifier.fontSize(1.3.cssRem).color(palette.textPrimary))
                                AppButton(
                                    text = if (user == null) s.signInToBuy else s.buy,
                                    onClick = { checkout(pack.paddlePriceId) },
                                    variant = AppButtonVariant.OUTLINE,
                                    fullWidth = true
                                )
                            }
                        }
                    }
                }
            }

            val costs = AuthState.usage?.creditCosts.orEmpty()
            if (costs.isNotEmpty()) {
                AppCard(Modifier.fillMaxWidth()) {
                    Column(Modifier.gap(0.4.cssRem)) {
                        SpanText(s.creditCostsTitle, Modifier.fontWeight(FontWeight.SemiBold).color(palette.textPrimary))
                        costs.forEach { (kind, credits) ->
                            val label = when (kind) {
                                "LUAU_SCRIPT" -> Txt.assetTypes.name(dev.danko.roblox_craft_ai.shared.model.AssetType.LUAU_SCRIPT)
                                "ROJO_PROJECT" -> Txt.assetTypes.name(dev.danko.roblox_craft_ai.shared.model.AssetType.ROJO_PROJECT)
                                "ANIMATION" -> Txt.assetTypes.name(dev.danko.roblox_craft_ai.shared.model.AssetType.ANIMATION)
                                "MESH_3D" -> "${Txt.assetTypes.name(dev.danko.roblox_craft_ai.shared.model.AssetType.MESH_3D)} · ${Txt.quality.meshStandard}"
                                "MESH_3D_HIGH" -> "${Txt.assetTypes.name(dev.danko.roblox_craft_ai.shared.model.AssetType.MESH_3D)} · ${Txt.quality.meshHigh}"
                                "MESH_3D_MAX" -> "${Txt.assetTypes.name(dev.danko.roblox_craft_ai.shared.model.AssetType.MESH_3D)} · ${Txt.quality.meshMax}"
                                "CONCEPT" -> "${Txt.concept.stepTitle} · ${Txt.quality.sketchStandard}"
                                "CONCEPT_REROLL" -> "${Txt.concept.regenerate} · ${Txt.quality.sketchStandard}"
                                "CONCEPT_HIGH" -> "${Txt.concept.stepTitle} · ${Txt.quality.sketchHigh}"
                                "REFACTOR" -> Txt.chat.aiAssistant
                                else -> kind
                            }
                            SpanText("$label — ⚡ $credits", Modifier.fontSize(0.85.cssRem).color(palette.textSecondary))
                        }
                    }
                }
            }

            Column(Modifier.gap(0.4.cssRem)) {
                SpanText(Txt.legal.checkoutConsent, Modifier.fontSize(0.85.cssRem).color(palette.textSecondary))
                LegalLinks(listOf(LegalDoc.TERMS, LegalDoc.REFUNDS, LegalDoc.PRIVACY), Modifier.fontSize(0.85.cssRem))
            }
        }
    }
    SiteFooter()
}
